import React, { useState, useRef } from 'react';
import StageVisualizer from './components/StageVisualizer';
import QueryForm from './components/QueryForm';
import './App.css';

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [stageDescription, setStageDescription] = useState('');
  const [stageData, setStageData] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [canSkip, setCanSkip] = useState(false);
  const streamControlRef = useRef({ paused: false, skipToNext: false });

  const handleQuerySubmit = async (query) => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    setCurrentStage(null);
    setStageDescription('');
    setStageData(null);
    setIsPaused(false);
    setCanSkip(true);
    streamControlRef.current = { paused: false, skipToNext: false };

    try {
      const apiUrl = process.env.REACT_APP_API_URL 
        ? process.env.REACT_APP_API_URL + '/api/process-query'
        : '/api/process-query';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });

      if (response.ok) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (line.trim()) {
              // Check if paused
              while (streamControlRef.current.paused && !streamControlRef.current.skipToNext) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }
              
              // Check if skip requested
              if (streamControlRef.current.skipToNext) {
                streamControlRef.current.skipToNext = false;
              }
              
              try {
                const data = JSON.parse(line);
                console.log('Received stage:', data.stage, 'with data:', data);
                setCurrentStage(data.stage);
                setStageDescription(data.description);
                setStageData(data);
                
                // If this is the final response stage, stop allowing skips
                if (data.stage === 'response') {
                  console.log('FINAL STAGE REACHED - Answer:', data.final_answer?.substring(0, 100));
                  setCanSkip(false);
                  streamControlRef.current.paused = false;
                  setIsPaused(false);
                } else if (data.stage === 'error') {
                  console.error('ERROR STAGE RECEIVED:', {
                    error: data.error,
                    error_type: data.error_type,
                    description: data.description
                  });
                  setCanSkip(false);
                  streamControlRef.current.paused = false;
                  setIsPaused(false);
                }
              } catch (e) {
                console.error('Error parsing response:', e);
              }
            }
          }
        }
      } else {
        console.error('API response not OK:', response.status);
        const errorText = await response.text();
        console.error('Error body:', errorText);
        setCurrentStage('error');
        setStageDescription('Server error: ' + response.status);
        setIsLoading(false);
        setCanSkip(false);
      }
    } catch (error) {
      console.error('Error fetching API:', error);
      setCurrentStage('error');
      setStageDescription('Error connecting to server. Please try again.');
      setIsLoading(false);
      setCanSkip(false);
    } finally {
      // Keep loading state briefly then turn off to show final answer
      setTimeout(() => {
        console.log('Turning off loading state. Current stage:', currentStage);
        setIsLoading(false);
        setCanSkip(false);
      }, 300);
    }
  };

  const handlePauseResume = () => {
    setIsPaused(!isPaused);
    streamControlRef.current.paused = !isPaused;
  };

  const handleNextStage = () => {
    streamControlRef.current.skipToNext = true;
    streamControlRef.current.paused = false;
    setIsPaused(false);
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="header-content">
          <img src="/qub-logo.svg" alt="Queen's University Belfast" className="qub-logo" />
          <div className="header-text">
            <h1>How AI Answers Your Questions?</h1>
            <p>An Interactive Journey Through the Cloud-AI Pipeline</p>
          </div>
          <img src="/QUB_180_Years_Logo_Red_Landscape_RGB.png" alt="QUB 180 Years" className="qub-logo-right" />
        </div>
      </header>

      <div className="app-content">
        <div className="query-section">
          <QueryForm 
            onSubmit={handleQuerySubmit}
            isLoading={isLoading}
          />
          
          {isLoading && canSkip && (
            <div className="controls-bar">
              <button 
                className="control-btn" 
                onClick={handlePauseResume}
                disabled={!canSkip}
              >
                {isPaused ? '▶️ Resume' : '⏸️ Pause'}
              </button>
              <button 
                className="control-btn" 
                onClick={handleNextStage}
                disabled={!canSkip}
              >
                ⏭️ Next Stage
              </button>
            </div>
          )}
          
          {!isLoading && currentStage === 'response' && (
            <div className="controls-bar">
              <button 
                className="control-btn success-btn" 
                onClick={() => {
                  setCurrentStage(null);
                  setStageData(null);
                  setStageDescription('');
                }}
              >
                ✨ Ask Another Question
              </button>
            </div>
          )}
        </div>

        <div className="visualizer-container">
          {currentStage ? (
            <StageVisualizer 
              stage={currentStage}
              description={stageDescription}
              data={stageData}
            />
          ) : (
            <div className="welcome-message">
              <h2>👋 Ask any questions you have!</h2>
              <p>Watch as your question travels through the Cloud-AI pipeline</p>
            </div>
          )}
        </div>
      </div>

      <footer className="app-footer">
        Conceptualised &amp; Developed by Dr Esha Barlaskar &nbsp;|&nbsp; EEECS &nbsp;|&nbsp; QUB &nbsp;|&nbsp; <a href="mailto:e.barlaskar@qub.ac.uk">e.barlaskar@qub.ac.uk</a>
      </footer>
    </div>
  );
}

export default App;
